import { createForm } from '@seek/forms-ui';
import React, { useEffect, useState } from 'react';
import { useLanguage } from '@vocab/react';
import type { Language } from '@seek/ca-myprofile-utils';

import {
  UserActionType,
  type ChangeEmailModel,
} from 'src/reducers/user/actionCreators';
import showCustomerServiceView from 'src/modules/sso/showCustomerServiceView';
import type { ErrorMessage } from 'src/types';
import Form, { FORM_NAME } from './Form';
import type { ChangeEmailAddressFailure } from 'src/modules/graphql/types';
import { useDispatch } from 'react-redux';
import { useApiClient } from 'src/modules/ApiProvider';
import { PageUpdateActionType } from 'src/reducers/page/actionCreators';

const { FormProvider, useField } = createForm<ChangeEmailModel>(FORM_NAME, {
  formType: 'braid',
});
export type EmailUseField = typeof useField;

const ChangeEmailForm = ({
  toggle,
  setSuccess,
}: {
  toggle: () => void;
  setSuccess: (state: boolean) => void;
}) => {
  const [customerServiceView, setCustomerServiceView] = useState(false);
  useEffect(() => {
    showCustomerServiceView().then(setCustomerServiceView);
  }, []);

  const { language } = useLanguage();
  const dispatch = useDispatch();
  const api = useApiClient();

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = useState<ErrorMessage>();

  const onSubmit = (formValues: ChangeEmailModel) => {
    setSubmitting(true);

    api
      .changeEmail(formValues)
      .then((result: any) => {
        if (result?.__typename === 'ChangeEmailAddressSuccess') {
          // Update to new email
          dispatch({
            type: UserActionType.UPDATE_USER_STATE,
            payload: {
              emailAddress: formValues.newEmail,
            },
          });

          // Sends analytics
          dispatch({
            type: PageUpdateActionType.SET_CHANGE_EMAIL_SUCCESS,
          });

          toggle();
          setSuccess(true);
        } else if (result?.__typename === 'ChangeEmailAddressFailure') {
          const failure = result as ChangeEmailAddressFailure;

          if (failure.error.__typename === 'AccountValidationError') {
            setError(
              failure.error?.message ?? failure.error?.details[0]?.message,
            );
          } else {
            setError(failure.error.message);
          }
        }
        setSubmitting(false);
      })
      .catch((err: any) => {
        setError(err?.message || 'Oops!');
        setSubmitting(false);
      });

    return;
  };

  return (
    <FormProvider
      initialValues={{
        newEmail: '',
        confirmEmail: '',
        currentPassword: '',
      }}
      language={language as Language}
    >
      {({ handleSubmit }) => (
        <Form
          onSubmit={handleSubmit(onSubmit)}
          submitting={submitting}
          toggle={toggle}
          error={error}
          useField={useField}
          showCustomerServiceView={customerServiceView}
        />
      )}
    </FormProvider>
  );
};

export default ChangeEmailForm;
